/**
 * Web Vitals Reporter
 *
 * Integrates the web-vitals library with Application Insights for Real User Monitoring (RUM).
 * Tracks Core Web Vitals metrics and sends them to Application Insights for analysis.
 *
 * Issue #1004 - Phase 3: Performance Monitoring & Automation
 * @see https://web.dev/vitals/
 */

import appInsightsService from '../services/appInsightsService.js';
import Logger from './logger.js';

/**
 * Initialize Web Vitals tracking with Application Insights
 *
 * This function should be called once during app initialization.
 * It imports the web-vitals library dynamically and sets up tracking
 * for all Core Web Vitals metrics.
 *
 * @returns {Promise<void>}
 */
export async function initializeWebVitalsTracking() {
  // Skip in test environment
  if (process.env.NODE_ENV === 'test') {
    Logger.debug('[WebVitals] Skipping initialization in test environment');
    return;
  }

  // Skip if Application Insights is not configured
  if (!import.meta.env.VITE_APPINSIGHTS_KEY) {
    Logger.debug('[WebVitals] Skipping - Application Insights not configured');
    return;
  }

  try {
    // Dynamic import for code splitting - web-vitals only needed in production
    const { onCLS, onINP, onFCP, onLCP, onTTFB } = await import('web-vitals');

    // Create the reporter callback
    const reporter = createWebVitalsReporter();

    // Register all Core Web Vitals
    // Note: FID was replaced by INP as a Core Web Vital in March 2024
    onCLS(reporter); // Cumulative Layout Shift
    onINP(reporter); // Interaction to Next Paint (replaced FID)
    onFCP(reporter); // First Contentful Paint
    onLCP(reporter); // Largest Contentful Paint
    onTTFB(reporter); // Time to First Byte

    Logger.info('[WebVitals] Core Web Vitals tracking initialized');
  } catch (error) {
    Logger.error('[WebVitals] Failed to initialize tracking:', error);
  }
}

/**
 * Create a reporter callback for web-vitals
 *
 * This callback receives metrics from the web-vitals library and
 * forwards them to Application Insights via appInsightsService.
 *
 * @returns {Function} Reporter callback for web-vitals
 */
export function createWebVitalsReporter() {
  return metric => {
    try {
      // Use the appInsightsService method to track the web vital
      appInsightsService.trackWebVital(metric);

      // Log in development for debugging
      if (import.meta.env.MODE === 'development') {
        const rating = metric.rating || getRating(metric.name, metric.value);
        Logger.debug(`[WebVitals] ${metric.name}: ${formatMetricValue(metric.name, metric.value)} (${rating})`);
      }
    } catch (error) {
      Logger.warn('[WebVitals] Failed to report metric:', error);
    }
  };
}

/**
 * Get rating for a metric value based on Core Web Vitals thresholds
 *
 * @param {string} name - Metric name (CLS, INP, FCP, LCP, TTFB)
 * @param {number} value - Metric value
 * @returns {string} Rating: 'good', 'needs-improvement', or 'poor'
 */
function getRating(name, value) {
  const thresholds = {
    CLS: { good: 0.1, poor: 0.25 },
    INP: { good: 200, poor: 500 },
    FCP: { good: 1800, poor: 3000 },
    LCP: { good: 2500, poor: 4000 },
    TTFB: { good: 800, poor: 1800 }
  };

  const threshold = thresholds[name];
  if (!threshold) return 'unknown';

  if (value <= threshold.good) return 'good';
  if (value > threshold.poor) return 'poor';
  return 'needs-improvement';
}

/**
 * Format metric value for display
 *
 * @param {string} name - Metric name
 * @param {number} value - Metric value
 * @returns {string} Formatted value with appropriate unit
 */
function formatMetricValue(name, value) {
  if (name === 'CLS') {
    return value.toFixed(3);
  }
  return `${Math.round(value)}ms`;
}

/**
 * Track a custom performance metric
 *
 * Use this for application-specific performance measurements
 * that aren't covered by Core Web Vitals.
 *
 * @param {string} name - Metric name (will be prefixed with 'Custom.')
 * @param {number} value - Metric value in milliseconds
 * @param {Object} properties - Additional properties to track
 */
export function trackCustomMetric(name, value, properties = {}) {
  appInsightsService.trackMetric(`Custom.${name}`, value, {
    ...properties,
    url: window.location.pathname,
    timestamp: new Date().toISOString()
  });
}

/**
 * Track page load performance
 *
 * Captures Navigation Timing API metrics for detailed page load analysis.
 * Should be called after the page has fully loaded.
 */
export function trackPageLoadPerformance() {
  if (typeof window === 'undefined' || !window.performance) {
    return;
  }

  // Wait for the load event to complete
  if (document.readyState !== 'complete') {
    window.addEventListener('load', () => {
      setTimeout(trackPageLoadPerformance, 0);
    });
    return;
  }

  try {
    const navigation = performance.getEntriesByType('navigation')[0];
    // Validate navigation entry has expected PerformanceNavigationTiming properties
    if (
      !navigation ||
      typeof navigation.domainLookupStart !== 'number' ||
      typeof navigation.domainLookupEnd !== 'number' ||
      typeof navigation.connectStart !== 'number' ||
      typeof navigation.connectEnd !== 'number' ||
      typeof navigation.requestStart !== 'number' ||
      typeof navigation.responseStart !== 'number' ||
      typeof navigation.responseEnd !== 'number' ||
      typeof navigation.domInteractive !== 'number' ||
      typeof navigation.domContentLoadedEventEnd !== 'number' ||
      typeof navigation.startTime !== 'number' ||
      typeof navigation.loadEventEnd !== 'number'
    ) {
      Logger.warn('[WebVitals] Navigation timing entry missing expected properties, skipping page load metrics.');
      return;
    }

    const metrics = {
      // DNS lookup time
      dnsLookup: navigation.domainLookupEnd - navigation.domainLookupStart,
      // TCP connection time
      tcpConnect: navigation.connectEnd - navigation.connectStart,
      // Time to first byte (server response time)
      serverResponse: navigation.responseStart - navigation.requestStart,
      // DOM parsing time
      domParsing: navigation.domInteractive - navigation.responseEnd,
      // DOM content loaded
      domContentLoaded: navigation.domContentLoadedEventEnd - navigation.startTime,
      // Full page load
      pageLoad: navigation.loadEventEnd - navigation.startTime
    };

    // Track each metric
    Object.entries(metrics).forEach(([name, value]) => {
      if (value > 0) {
        trackCustomMetric(`PageLoad.${name}`, value);
      }
    });

    Logger.debug('[WebVitals] Page load metrics tracked:', metrics);
  } catch (error) {
    Logger.warn('[WebVitals] Failed to track page load performance:', error);
  }
}

export default {
  initializeWebVitalsTracking,
  createWebVitalsReporter,
  trackCustomMetric,
  trackPageLoadPerformance
};
